import { Children } from "react";

import {
  Chip,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  styled,
  // Box,
} from "@mui/material";
import { memo } from "utils/memo";
import T from "utils/constants/T";
import { get } from "lodash";
import { dateFormat } from "utils/date";
import { format } from "date-fns";
import { formatAmount } from "utils/CommonMethods";
import { WEBHOOK_ENABLED_SYSTEMS } from "utils/constants/common";
import { useSelector } from "react-redux";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: "inherit",
  overflow: "hidden",
  padding: "8px 0px 8px 10px",
}));

interface TimesheetBodyProps {
  allTableRows: any[];
  page: number;
  rowsPerPage: number;
  totalTableRowsCount: number;
  totalPageCount: number;
  systemPayrollCode: string;
}

const TimesheetBody = ({
  allTableRows = [],
  page,
  rowsPerPage,
  totalTableRowsCount,
  totalPageCount,
  systemPayrollCode,
}: TimesheetBodyProps) => {
  const isLoading = useSelector((state: any) => state.timesheet?.loader);

  const workingHours = (timesheetRecord: any) => {
    const formatWorkingHours = (startTime: string | number | Date, endTime: string | number | Date) => {
      if (!startTime || !endTime) return "N/A";
      return `${format(new Date(startTime), "hh:mm a")} - ${format(
        new Date(endTime),
        "hh:mm a"
      )}`;
    };

    const astuteWorkingHours = () => {
      const { startTime, endTime } = timesheetRecord || {};
      return formatWorkingHours(startTime, endTime);
    };

    const keypayWorkingHours = () => {
      const { startTime, endTime } = timesheetRecord || {};
      return formatWorkingHours(startTime, endTime);
    };

    const deputyWorkingHours = () => {
      const { StartTimeLocalized, EndTimeLocalized, _DPMetaData } =
        timesheetRecord || {};
      if (!StartTimeLocalized || !EndTimeLocalized) return "N/A";

      const formattedHours = formatWorkingHours(StartTimeLocalized, EndTimeLocalized);
      const companyName = _DPMetaData?.OperationalUnitInfo?.OperationalUnitName || "";

      return companyName ? `${formattedHours} at ${companyName}` : formattedHours;
    };

    const xeroWorkingHours = () => {
      const { StartDateTimestamp, EndDateTimestamp, Hours, TimesheetLines } = timesheetRecord || {};
      if (Hours != null && Hours !== undefined && Hours > 0) return `${Hours} hrs`;
      if (TimesheetLines && TimesheetLines.length > 0) {
        let totalHours = 0;
        for (const line of TimesheetLines) {
          if (Array.isArray(line.NumberOfUnits)) {
            totalHours += line.NumberOfUnits.reduce((acc: number, val: any) => acc + (Number(val) || 0), 0);
          }
        }
        if (totalHours > 0) return `${totalHours} hrs`;
      }
      return formatWorkingHours(StartDateTimestamp, EndDateTimestamp);
    };

    const myobWorkingHours = () => {
      return timesheetRecord.hoursWorked;
    };

    const timesiteProWorkingHours = () => {
      return timesheetRecord.workedHours;
    };

    if (systemPayrollCode.toLowerCase() === "astute")
      return astuteWorkingHours(); // hudson
    else if (systemPayrollCode.toLowerCase() === "deputy")
      return deputyWorkingHours(); //challenger
    else if (systemPayrollCode.toLowerCase() === "keypay")
      return keypayWorkingHours(); //expedo
    else if (systemPayrollCode.toLowerCase() === "myobexo")
      return myobWorkingHours();
    else if (systemPayrollCode.toLowerCase() === "xero")
      return xeroWorkingHours();
    else if (systemPayrollCode.toLowerCase() === "timesite_pro")
      return timesiteProWorkingHours();
    else if (WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode))
      return timesheetRecord.workedHours;
  };

  const payRate = (timesheetRecord: any) => {
    const astutePayRate = () => {
      const salaryEarningRate = get(
        timesheetRecord,
        "employee[0].xero_salary_earnings_rates",
        ""
      );
      if (!salaryEarningRate) return "N/A";

      const earningObject = salaryEarningRate[salaryEarningRate.length - 1];
      if (!earningObject) return "N/A";

      return `${formatAmount(earningObject.rate)}/hrs`;
    };

    const deputyPayRate = () => {
      if (!timesheetRecord.PayRuleObject) return "N/A";
      return `$${timesheetRecord.PayRuleObject.HourlyRate}/hrs(${timesheetRecord.PayRuleObject.PayTitle})`;
    };

    const timesiteProPayRate = () => {
      if (!timesheetRecord.hourlyRateCost) return "N/A";
      return `$${timesheetRecord.hourlyRateCost}`;
    };

    const keypayPayRate = () => {
      const payrate = get(timesheetRecord, "employee[0].rate_per_hour", "N/A");
      const keypay_primary_pay_category = get(
        timesheetRecord,
        "employee[0].keypay_primary_pay_category",
        "N/A"
      );
      if (payrate === "N/A") return "N/A";
      return `$${payrate}/hrs(${keypay_primary_pay_category})`;
    };

    const myobPayRate = () => {
      return timesheetRecord.PayRate ? `${timesheetRecord.PayRate}/hrs` : "N/A";
    };

    const xeroPayRate = () => {
      let payrate = get(
        timesheetRecord,
        "TimesheetLines[0].EarningsRate.RatePerUnit",
        "N/A"
      );
      if (payrate === "N/A" || payrate === undefined || payrate === null || payrate === 0) {
        const earningsLines = get(timesheetRecord, "Employee.PayTemplate.EarningsLines", []);
        const earningsRateId = get(timesheetRecord, "TimesheetLines[0].EarningsRateID");
        if (earningsLines.length > 0 && earningsRateId) {
          const matchedLine = earningsLines.find((e: any) => e.EarningsRateID === earningsRateId);
          if (matchedLine?.RatePerUnit > 0) {
            payrate = matchedLine.RatePerUnit;
          }
        }
      }
      if (payrate === "N/A" || payrate === undefined || payrate === null || payrate === 0) return "N/A";
      return `$${payrate}/hrs`;
    };

    const webhookPayRate = () => {
      const payrate = get(
        timesheetRecord,
        "employee.rate",
        0
      );
      if (!payrate) return "N/A"
      return `${payrate} / day`
    }

    if (systemPayrollCode.toLowerCase() === "astute") return astutePayRate();
    else if (systemPayrollCode.toLowerCase() === "deputy")
      return deputyPayRate();
    else if (systemPayrollCode.toLowerCase() === "keypay")
      return keypayPayRate();
    else if (systemPayrollCode.toLowerCase() === "myobexo")
      return myobPayRate();
    else if (systemPayrollCode.toLowerCase() === "timesite_pro")
      return timesiteProPayRate()
    else if (systemPayrollCode.toLowerCase() === "xero") return xeroPayRate();
    else if (WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode)) return webhookPayRate()
  };

  const getStatus = (statusInput: number | string): any => {
    const statusLists = [
      {
        index: 0,
        value: "PENDING",
        type: "info",
      },
      {
        index: 1,
        value: "APPROVED",
        type: "success",
      },
      {
        index: 2,
        value: "ACCEPTED",
        type: "success",
      },
      {
        index: 3,
        value: "REJECTED",
        type: "error",
      },
      {
        index: 4,
        value: "PENDING",
        type: "info",
      },
      {
        index: 5,
        value: "SUBMITTED",
        type: "success",
      },
      {
        index: 6,
        value: "COMPLETED",
        type: "success",
      },
    ];

    const statusListsIndex = statusLists.findIndex((list) => {
      if (typeof statusInput === "number") {
        return list.index === statusInput;
      }
      if (typeof statusInput === "string") {
        const upper = statusInput.toUpperCase();
        return list.value === upper || String(list.index) === upper;
      }
      return false;
    });

    if (statusListsIndex < 0) return statusLists[0];
    else return statusLists[statusListsIndex];
  };

  const cashdStatus = (timesheetRecord: any) => {
    const getCashdStatusChip = (statusKey = "CashDStatus") => {
      const cashdStatus = get(timesheetRecord, statusKey, 0);
      const status = getStatus(cashdStatus);

      return (
        <Chip
          color={status.type}
          label={status.value}
          size="small"
          variant="light"
        />
      );
    };

    const astuteCashdStatus = () => getCashdStatusChip();
    const deputyCashdStatus = () => getCashdStatusChip();
    const webhookCashdStatus = () => getCashdStatusChip();
    const keypayCashdStatus = () => getCashdStatusChip("CasdDStatus");
    const xeroCashdStatus = () => getCashdStatusChip();
    const myobexoCashdStatus = () => getCashdStatusChip();
    const timesiteProCashdStatus = () => getCashdStatusChip();
    const myobCashdStatus = () => (
      <Chip color="info" label="PENDING" size="small" variant="light" />
    );

    const payrollStatusMap: Record<string, () => React.JSX.Element> = {
      myobexo: myobexoCashdStatus,
      myob: myobCashdStatus,
      xero: xeroCashdStatus,
      astute: astuteCashdStatus,
      deputy: deputyCashdStatus,
      keypay: keypayCashdStatus,
      timesite_pro: timesiteProCashdStatus
    };
    let statusMethodInstance = payrollStatusMap[systemPayrollCode.toLowerCase()];

    if (!statusMethodInstance && WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode)) {
      return webhookCashdStatus();
    }
    return statusMethodInstance?.() || null;
  };

  const timesheetDate = (timesheetRecord: any) => {
    const getTimeSheetDate = () => {
      const startDate = dateFormat(get(timesheetRecord, "StartDate", ""));
      const endDate = dateFormat(get(timesheetRecord, "EndDate", ""));
      return `${startDate} to ${endDate}`;
    }

    const myobexoTimesheetDate = () => {
      const startDate = dateFormat(get(timesheetRecord, "StartDate", ""));
      // const endDate = dateFormat(get(timesheetRecord, "EndDate", ""));
      return `${startDate}`;
    }

    const astuteTimesheetDate = () => dateFormat(get(timesheetRecord, "date", ""));
    const deputyTimesheetDate = () => dateFormat(get(timesheetRecord, "Date", ""));
    const keypayTimesheetDate = () => dateFormat(get(timesheetRecord, "startTime", ""));
    const myobTimesheetDate = () => myobexoTimesheetDate();
    const xeroTimesheetDate = () => getTimeSheetDate();
    const timesiteProTimesheetDate = () => dateFormat(get(timesheetRecord, "startTime", ""));
    const webhookTimesheetDate = () => dateFormat(get(timesheetRecord, "startTime", ""));

    if (systemPayrollCode.toLowerCase() === "astute")
      return astuteTimesheetDate();
    else if (systemPayrollCode.toLowerCase() === "deputy")
      return deputyTimesheetDate();
    else if (systemPayrollCode.toLowerCase() === "keypay")
      return keypayTimesheetDate();
    else if (systemPayrollCode.toLowerCase() === "myobexo" || systemPayrollCode.toLowerCase() === "myob")
      return myobTimesheetDate();
    else if (systemPayrollCode.toLowerCase() === "timesite_pro")
      return timesiteProTimesheetDate()
    else if (systemPayrollCode.toLowerCase() === "xero")
      return xeroTimesheetDate();
    else if (WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode))
      return webhookTimesheetDate();
  };

  const timesheetEmployeeName = (timesheetRecord: any) => {
    if (systemPayrollCode.toLowerCase() === "myobexo" || systemPayrollCode.toLowerCase() === "myob")
      return get(timesheetRecord, "StaffId.fullname", "") || get(timesheetRecord, "staff.fullname", "") || get(timesheetRecord, "Employee.Name", "");
    else if (systemPayrollCode.toLowerCase() === "xero") {
      const firstName = get(timesheetRecord, "Employee.FirstName", "");
      const lastName = get(timesheetRecord, "Employee.LastName", "");

      const name = firstName + " " + lastName;
      return name;
    } else {
      return get(timesheetRecord, "employee.fullname", "");
    }
  };

  const timesheetCompanyName = (timesheetRecord: any) => {
    return (
      get(timesheetRecord, "company.company_name") ||
      get(timesheetRecord, "company_name") ||
      get(timesheetRecord, "company_details.company_name") ||
      "-"
    );
  };

  return (
    <TableBody>
      {isLoading ? (
        <TableRow>

          <StyledTableCell colSpan={10} sx={{ border: "none" }}>
            <Typography variant="body1" textAlign="center">
              {T.LOADING}
            </Typography>
          </StyledTableCell>
        </TableRow>
      ) : (
        <>
        {allTableRows && allTableRows.length === 0 ? (
          <TableRow>
            <StyledTableCell colSpan={6} sx={{ border: "none" }}>
              <Typography variant="body1" textAlign="center">
                {T.NO_RECORDS}
              </Typography>
            </StyledTableCell>
          </TableRow>
        ) : (
          Children.toArray(
        allTableRows.map((record) => (
          <TableRow sx={{ background: "white" }} key={record?._id}>
            <StyledTableCell>{timesheetCompanyName(record)}</StyledTableCell>
            <StyledTableCell>{timesheetEmployeeName(record)}</StyledTableCell>
            <StyledTableCell>{timesheetDate(record)}</StyledTableCell>
            <StyledTableCell>{workingHours(record)}</StyledTableCell>
            <StyledTableCell>{payRate(record)}</StyledTableCell>
            <StyledTableCell>{cashdStatus(record)}</StyledTableCell>
          </TableRow>
        ))
          )
        )}
        </>
      )}
    </TableBody>
  );
};

export default memo(TimesheetBody);
